import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface People {
  total: number;
  adult: number;
  children: number;
}

interface TravelDetails {
  uuid: string;
  destination: string;
  dateOfTravel: string;
  durationInDays: number;
  people: People;
  category: string;
  budget: string;
}

interface Location {
  pincode: string;
  name: string;
  description: string;
  type: string;
  isBusiness: boolean;
  cost: string;
  district: string;
  city: string;
  state: string;
  rating: string;
  reviews: string;
  coordinates: {
    lng: number;
    lat: number;
  };
  nearByPublicTravels: {
    airport: {
      name: string;
      distance: string;
      duration: string;
      icon: string;
    }[];
    train: { name: string; distance: string; duration: string; icon: string }[];
  };
  attractions: {
    name: string;
    description: string;
    imageUrl: string;
  };
}

interface Header {
  title: string;
  location: Location[];
}

interface Activity {
  time: string;
  title: string;
  description: string;
  cost: string;
  icon: string;
  notes?: string;
  locations?: Location[];
}

export interface Facts {
  title: string;
  description: string;
}

interface CostItem {
  title: string;
  type: string;
  avgCost: string;
  minCost: string;
  maxCost: string;
}

interface TotalCost {
  avgCost: string;
  minCost: string;
  maxCost: string;
}

interface TripCost {
  title: string;
  costs: CostItem[];
  totalCost: TotalCost;
}

interface TravelItem {
  title: string;
  description: string;
  day: number;
  activities: Activity[];
}

interface MissingDetail {
  field: string;
  prompt: string;
  inputType: string;
  options: [] | null;
}

export interface SearchEntry {
  footer: string;
  input: string;
  header?: Header;
  title?: string;
  aiReply: string;
  displayType?: string;
  isTravelQuery?: boolean;
  canGenerateItinerary?: boolean;
  missingDetails: MissingDetail | null;
  travelDetails?: TravelDetails | null;
  travelItems?: TravelItem[];
  quickFacts?: Facts[];
  travelCosts?: TripCost;
}

interface ChatHistoryStore {
  searchHistory: SearchEntry[];
  addSearchEntry: (
    input: string,
    aiReply?: string,
    missingDetails?: MissingDetail | null,
    header?: Header,
    title?: string,
    footer?: string,
    displayType?: string,
    isTravelQuery?: boolean,
    travelDetails?: TravelDetails | null,
    travelItems?: TravelItem[],
    quickFacts?: Facts[],
    travelCosts?: TripCost
  ) => void;
  updateLastSearchEntry: (updatedEntry: Partial<SearchEntry>) => void;
  clearSearchHistory: () => void;
  clearLastEntry: () => void;
}

export const useChatHistoryStore = create<ChatHistoryStore>()(
  persist(
    set => ({
      searchHistory: [],

      addSearchEntry: (
        input,
        aiReply = '',
        missingDetails = null,
        header = undefined,
        title = '',
        footer = '',
        displayType = 'text',
        isTravelQuery = false,
        travelDetails: TravelDetails | null = null,
        travelItems: TravelItem[] = [],
        quickFacts: Facts[] = [],
        travelCosts: TripCost
      ) =>
        set(state => ({
          searchHistory: [
            ...state.searchHistory,
            {
              input,
              header,
              title,
              footer,
              aiReply,
              displayType,
              isTravelQuery,
              canGenerateItinerary: missingDetails === null,
              missingDetails,
              travelDetails,
              travelItems,
              quickFacts,
              travelCosts,
            },
          ],
        })),

      updateLastSearchEntry: updatedEntry =>
        set(state => {
          if (state.searchHistory.length === 0) return state;

          const updatedHistory = [...state.searchHistory];
          updatedHistory[updatedHistory.length - 1] = {
            ...updatedHistory[updatedHistory.length - 1],
            ...updatedEntry,
          };

          return { searchHistory: updatedHistory };
        }),
      clearLastEntry: () =>
        set(state => ({
          searchHistory: state.searchHistory.slice(0, -1),
        })),
      clearSearchHistory: () => set({ searchHistory: [] }),
    }),
    {
      name: 'chat-history-store',
    }
  )
);
